import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiArrowLeft } from 'react-icons/fi';
import api from '../../services/api';

const TruckForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('Basic');
  const [isDirty, setIsDirty] = useState(false);
  const [formData, setFormData] = useState({
    mbl_no: '',
    hbl_no: '',
    vessel_flight_no: '',
    customer: '',
    customer_ref_no: '',
    shipper: '',
    consignee: '',
    trucker: '',
    bill_to: '',
    post_date: new Date().toISOString().split('T')[0],
    ship_type: 'Ocean',
    etd: '',
    port_of_discharge: '',
    eta: '',
    port_of_loading: '',
    final_destination: '',
    final_eta: '',
    empty_pickup: '',
    freight_pickup: '',
    delivery_to: '',
    empty_return: '',
    package_type: 'CARTON(S)',
    package_count: '',
    weight_kg: '',
    weight_lb: '',
    measurement_cbm: '',
    measurement_cft: '',
    estimated_delivery_date: '',
    delivered_date: '',
    delivered: false,
    memo: '',
    status: 'Pending',
  });

  useEffect(() => {
    if (isEdit) {
      loadShipment();
    }
  }, [id]);

  // Warn user about unsaved changes when leaving page
  useEffect(() => {
    if (!isDirty && isEdit) return;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty, isEdit]);

  const loadShipment = async () => {
    try {
      const response = await api.get(`/truck/${id}`);
      const data = response.data;
      setFormData({
        mbl_no: data.mbl_no || '',
        hbl_no: data.hbl_no || '',
        vessel_flight_no: data.vessel_flight_no || '',
        customer: data.customer || '',
        customer_ref_no: data.customer_ref_no || '',
        shipper: data.shipper || '',
        consignee: data.consignee || '',
        trucker: data.trucker || '',
        bill_to: data.bill_to || '',
        post_date: data.post_date ? data.post_date.split('T')[0] : '',
        ship_type: data.ship_type || 'Ocean',
        etd: data.etd ? data.etd.split('T')[0] : '',
        port_of_discharge: data.port_of_discharge || '',
        eta: data.eta ? data.eta.split('T')[0] : '',
        port_of_loading: data.port_of_loading || '',
        final_destination: data.final_destination || '',
        final_eta: data.final_eta ? data.final_eta.split('T')[0] : '',
        empty_pickup: data.empty_pickup || '',
        freight_pickup: data.freight_pickup || '',
        delivery_to: data.delivery_to || '',
        empty_return: data.empty_return || '',
        package_type: data.package_type || 'CARTON(S)',
        package_count: data.package_count || '',
        weight_kg: data.weight_kg || '',
        weight_lb: data.weight_lb || '',
        measurement_cbm: data.measurement_cbm || '',
        measurement_cft: data.measurement_cft || '',
        estimated_delivery_date: data.estimated_delivery_date ? data.estimated_delivery_date.split('T')[0] : '',
        delivered_date: data.delivered_date ? data.delivered_date.split('T')[0] : '',
        delivered: data.delivered || false,
        memo: data.memo || '',
        status: data.status || 'Pending',
      });
    } catch (error) {
      toast.error('Failed to load shipment');
      navigate('/truck');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setIsDirty(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (isEdit) {
        await api.put(`/truck/${id}`, formData);
        toast.success('Shipment updated successfully');
      } else {
        const response = await api.post('/truck', formData);
        toast.success(`Shipment ${response.data.file_no} created successfully`);
      }
      navigate('/truck');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save shipment');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading"><div className="loading-spinner"></div></div>;
  }

  return (
    <div className="shipment-page">
      {/* Breadcrumb */}
      <div className="shipment-breadcrumb">
        <button className="breadcrumb-back-btn" onClick={() => {
          if (isDirty && !window.confirm('You are leaving the page without saving. Are you sure?')) return;
          navigate('/truck');
        }} title="Back">
          <FiArrowLeft />
        </button>
        <span>Home</span>
        <span className="breadcrumb-sep">&gt;</span>
        <span>Truck</span>
        <span className="breadcrumb-sep">&gt;</span>
        <span className="breadcrumb-current">{isEdit ? 'Edit Shipment' : 'New Shipment'}</span>
      </div>

      {/* Tab Bar */}
      <div className="shipment-tabs">
        {['Basic', 'Container', 'Accounting', 'Doc Center'].map(tab => (
          <button
            key={tab}
            className={`shipment-tab ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Form Content */}
      {activeTab === 'Basic' && (
        <form onSubmit={handleSubmit} className="shipment-form">

          {/* General Info */}
          <div className="form-section-header">General Information</div>
          <div className="shipment-form-grid">
            <div className="shipment-form-group">
              <label>File No.</label>
              <input type="text" value="(Auto Generated)" disabled />
            </div>
            <div className="shipment-form-group">
              <label>Post Date</label>
              <input type="date" name="post_date" value={formData.post_date} onChange={handleChange} />
            </div>
            <div className="shipment-form-group">
              <label>Status</label>
              <select name="status" value={formData.status} onChange={handleChange}>
                <option value="Pending">Pending</option>
                <option value="In Transit">In Transit</option>
                <option value="Delivered">Delivered</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>
            <div className="shipment-form-group">
              <label>Ship Type</label>
              <select name="ship_type" value={formData.ship_type} onChange={handleChange}>
                <option value="Ocean">Ocean</option>
                <option value="Air">Air</option>
                <option value="Land">Land</option>
              </select>
            </div>
          </div>

          <div className="shipment-form-grid">
            <div className="shipment-form-group">
              <label>MB/L No.</label>
              <input type="text" name="mbl_no" value={formData.mbl_no} onChange={handleChange} />
            </div>
            <div className="shipment-form-group">
              <label>HB/L No.</label>
              <input type="text" name="hbl_no" value={formData.hbl_no} onChange={handleChange} />
            </div>
            <div className="shipment-form-group">
              <label>Vessel/Flight No.</label>
              <input type="text" name="vessel_flight_no" value={formData.vessel_flight_no} onChange={handleChange} />
            </div>
            <div className="shipment-form-group">
              <label>Customer Ref No.</label>
              <input type="text" name="customer_ref_no" value={formData.customer_ref_no} onChange={handleChange} />
            </div>
          </div>

          <div className="shipment-section-divider"></div>

          {/* Parties */}
          <div className="form-section-header">Parties</div>
          <div className="shipment-form-grid">
            <div className="shipment-form-group">
              <label>Customer</label>
              <input type="text" name="customer" value={formData.customer} onChange={handleChange} />
            </div>
            <div className="shipment-form-group">
              <label>Shipper</label>
              <input type="text" name="shipper" value={formData.shipper} onChange={handleChange} />
            </div>
            <div className="shipment-form-group">
              <label>Consignee</label>
              <input type="text" name="consignee" value={formData.consignee} onChange={handleChange} />
            </div>
            <div className="shipment-form-group">
              <label>Bill To</label>
              <input type="text" name="bill_to" value={formData.bill_to} onChange={handleChange} />
            </div>
          </div>

          <div className="shipment-form-grid">
            <div className="shipment-form-group">
              <label>Trucker</label>
              <input type="text" name="trucker" value={formData.trucker} onChange={handleChange} />
            </div>
          </div>

          <div className="shipment-section-divider"></div>

          {/* Route Info */}
          <div className="form-section-header">Route Information</div>
          <div className="shipment-form-grid">
            <div className="shipment-form-group">
              <label>ETD</label>
              <input type="date" name="etd" value={formData.etd} onChange={handleChange} />
            </div>
            <div className="shipment-form-group">
              <label>Port of Loading</label>
              <input type="text" name="port_of_loading" value={formData.port_of_loading} onChange={handleChange} />
            </div>
            <div className="shipment-form-group">
              <label>ETA</label>
              <input type="date" name="eta" value={formData.eta} onChange={handleChange} />
            </div>
            <div className="shipment-form-group">
              <label>Port of Discharge</label>
              <input type="text" name="port_of_discharge" value={formData.port_of_discharge} onChange={handleChange} />
            </div>
          </div>

          <div className="shipment-form-grid">
            <div className="shipment-form-group">
              <label>Final Destination</label>
              <input type="text" name="final_destination" value={formData.final_destination} onChange={handleChange} />
            </div>
            <div className="shipment-form-group">
              <label>Final ETA</label>
              <input type="date" name="final_eta" value={formData.final_eta} onChange={handleChange} />
            </div>
          </div>

          <div className="shipment-section-divider"></div>

          {/* Delivery Info */}
          <div className="form-section-header">Delivery</div>
          <div className="shipment-form-grid">
            <div className="shipment-form-group">
              <label>Empty Pickup</label>
              <input type="text" name="empty_pickup" value={formData.empty_pickup} onChange={handleChange} />
            </div>
            <div className="shipment-form-group">
              <label>Freight Pickup</label>
              <input type="text" name="freight_pickup" value={formData.freight_pickup} onChange={handleChange} />
            </div>
            <div className="shipment-form-group">
              <label>Delivery To</label>
              <input type="text" name="delivery_to" value={formData.delivery_to} onChange={handleChange} />
            </div>
            <div className="shipment-form-group">
              <label>Empty Return</label>
              <input type="text" name="empty_return" value={formData.empty_return} onChange={handleChange} />
            </div>
          </div>

          <div className="shipment-form-grid">
            <div className="shipment-form-group">
              <label>Estimated Delivery</label>
              <input type="date" name="estimated_delivery_date" value={formData.estimated_delivery_date} onChange={handleChange} />
            </div>
            <div className="shipment-form-group">
              <label>Delivered</label>
              <div className="delivered-row">
                <input type="checkbox" name="delivered" checked={formData.delivered} onChange={handleChange} />
                <input type="date" name="delivered_date" value={formData.delivered_date} onChange={handleChange} />
              </div>
            </div>
          </div>

          <div className="shipment-section-divider"></div>

          {/* Cargo Details */}
          <div className="form-section-header">Cargo Details</div>
          <div className="shipment-form-grid">
            <div className="shipment-form-group">
              <label>Package</label>
              <div className="package-row">
                <select name="package_type" value={formData.package_type} onChange={handleChange}>
                  <option value="CARTON(S)">CARTON(S)</option>
                  <option value="PALLET(S)">PALLET(S)</option>
                  <option value="BOX(ES)">BOX(ES)</option>
                  <option value="CASE(S)">CASE(S)</option>
                  <option value="PIECE(S)">PIECE(S)</option>
                </select>
                <input type="number" name="package_count" value={formData.package_count} onChange={handleChange} placeholder="Qty" />
              </div>
            </div>
            <div className="shipment-form-group">
              <label>Weight</label>
              <div className="weight-row">
                <input type="number" step="0.01" name="weight_kg" value={formData.weight_kg} onChange={handleChange} placeholder="0.00" />
                <span className="unit">KG</span>
                <input type="number" step="0.01" name="weight_lb" value={formData.weight_lb} onChange={handleChange} placeholder="0.00" />
                <span className="unit">LB</span>
              </div>
            </div>
            <div className="shipment-form-group">
              <label>Measurement</label>
              <div className="measurement-row">
                <input type="number" step="0.01" name="measurement_cbm" value={formData.measurement_cbm} onChange={handleChange} placeholder="0.00" />
                <span className="unit">CBM</span>
                <input type="number" step="0.01" name="measurement_cft" value={formData.measurement_cft} onChange={handleChange} placeholder="0.00" />
                <span className="unit">CFT</span>
              </div>
            </div>
          </div>

          <div className="shipment-section-divider"></div>

          {/* Memo */}
          <div className="form-section-header">Memo</div>
          <div className="shipment-form-group">
            <textarea name="memo" value={formData.memo} onChange={handleChange} className="shipment-memo" placeholder="Add notes here..." />
          </div>

          {/* Actions */}
          <div className="shipment-form-actions">
            <button type="button" className="btn-cancel" onClick={() => {
              if (isDirty && !window.confirm('You are leaving the page without saving. Are you sure?')) return;
              navigate('/truck');
            }}>
              Cancel
            </button>
            <button type="submit" className="shipment-save-btn" disabled={saving}>
              {saving ? 'Saving...' : isEdit ? 'Update' : 'Create Shipment'}
            </button>
          </div>
        </form>
      )}

      {/* Container Tab - need to save shipment first */}
      {activeTab === 'Container' && (
        <div style={{ padding: '60px 40px', textAlign: 'center', color: '#94a3b8', background: 'white' }}>
          <p style={{ fontSize: '15px', fontWeight: 500 }}>Container</p>
          <p style={{ fontSize: '13px', marginTop: '8px' }}>Please save the shipment first to add containers.</p>
        </div>
      )}

      {/* Accounting Tab - need to save shipment first */}
      {activeTab === 'Accounting' && (
        <div style={{ padding: '60px 40px', textAlign: 'center', color: '#94a3b8', background: 'white' }}>
          <p style={{ fontSize: '15px', fontWeight: 500 }}>Accounting</p>
          <p style={{ fontSize: '13px', marginTop: '8px' }}>Please save the shipment first to manage invoices and costs.</p>
        </div>
      )}

      {/* Doc Center Tab - need to save shipment first */}
      {activeTab === 'Doc Center' && (
        <div style={{ padding: '60px 40px', textAlign: 'center', color: '#94a3b8', background: 'white' }}>
          <p style={{ fontSize: '15px', fontWeight: 500 }}>Doc Center</p>
          <p style={{ fontSize: '13px', marginTop: '8px' }}>Please save the shipment first to upload documents.</p>
        </div>
      )}
    </div>
  );
};

export default TruckForm;
